import { useTranslations } from "@/lib/useTranslations";
import type { ReactNode } from "react";

import type { ArticleAssigneeOption } from "@/core/services/types/article.types";
import { formatTeamMemberName } from "@/core/services/types/article.types";

const selectClassName =
  "w-full wireframe-border bg-neutral-50 px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

interface ArticleAssigneeFieldProps {
  members: ArticleAssigneeOption[];
  value: string;
  disabled: boolean;
  isPending: boolean;
  saveSpinner: ReactNode;
  fieldError?: string;
  error?: string;
  success: boolean;
  onChange: (value: string) => void;
}

export function ArticleAssigneeField({
  members,
  value,
  disabled,
  isPending,
  saveSpinner,
  fieldError,
  error,
  success,
  onChange,
}: ArticleAssigneeFieldProps) {
  const t = useTranslations("articles.edit");

  return (
    <section className="wireframe-border bg-white p-5 space-y-1.5">
      <label
        htmlFor="article-assigned-to"
        className="text-sm font-semibold text-whart-text"
      >
        {t("assignedToLabel")}
      </label>
      <div className="relative">
        <select
          id="article-assigned-to"
          value={value}
          disabled={disabled}
          aria-busy={isPending}
          onChange={(event) => onChange(event.target.value)}
          className={selectClassName}
        >
          <option value="">{t("assignedToUnassigned")}</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {formatTeamMemberName(member)}
            </option>
          ))}
        </select>
        {isPending ? (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {saveSpinner}
          </span>
        ) : null}
      </div>
      <FieldError message={fieldError} />
      {!success && error ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {error}
        </p>
      ) : null}
      {success ? (
        <p role="status" className="text-xs text-emerald-700">
          {t("saveAssignmentSuccess")}
        </p>
      ) : null}
    </section>
  );
}
